import type * as client from "../../../client";
import { formatAbbreviatedMoneyExact } from "../Money";

// Perils with a resolved deductible, mirroring the RMS AAL bucket structure.
// PSH is omitted because it shares the named_storm deductible function with
// PWH; we surface a single Named Storm entry.
export const DEDUCTIBLE_BUCKETS: { peril: string; label: string }[] = [
  { peril: "AOP", label: "AOP Ded" },
  { peril: "PWH", label: "Named Storm Ded" },
  { peril: "PES", label: "EQ Ded" },
  { peril: "PFL", label: "Flood Ded" },
  { peril: "PWB", label: "Wildfire Ded" },
  { peril: "PWT", label: "SCS Ded" },
];

export const LIMIT_BUCKETS: { peril: string; label: string }[] = [
  { peril: "AOP", label: "AOP Limit" },
  { peril: "PWH", label: "Named Storm Limit" },
  { peril: "PES", label: "EQ Limit" },
  { peril: "PFL", label: "Flood Limit" },
  { peril: "PWB", label: "Wildfire Limit" },
  { peril: "PWT", label: "SCS Limit" },
];

// Percentages are rounded to 4 decimal places. That is precise enough to keep
// real terms apart, and it merges float noise from the backend's resolution
// (0.049999… and 0.05 both read as 5%).
const formatPercentBin = (fraction: number): string =>
  `${parseFloat((fraction * 100).toFixed(4))}%`;

/** Pivot bin label for a deductible. */
export const formatDeductibleBin = (s: client.DeductibleSummary): string => {
  switch (s.kind) {
    case "site":
      return `${formatPercentBin(s.value)} site`;
    case "per_unit":
      return `${formatPercentBin(s.value)} per unit`;
    case "occurrence":
      return `${formatAbbreviatedMoneyExact(s.value)} per occ`;
    case "aggregate":
      return `${formatAbbreviatedMoneyExact(s.value)} agg`;
  }
};

/** Pivot bin label for a limit, or undefined when it caps nothing. Distinct
 *  terms get distinct labels. */
export const formatLimitBin = (s: client.LimitSummary): string | undefined => {
  const parts: string[] = [];
  if (s.occurrence != null) {
    parts.push(`${formatAbbreviatedMoneyExact(s.occurrence)} occ`);
  }
  if (s.aggregate != null) {
    parts.push(`${formatAbbreviatedMoneyExact(s.aggregate)} agg`);
  }
  return parts.length > 0 ? parts.join(", ") : undefined;
};

const DEDUCTIBLE_KIND_ORDER: client.DeductibleSummary["kind"][] = [
  "site",
  "per_unit",
  "occurrence",
  "aggregate",
];

/** Orders deductibles with percentage kinds first, then by value. */
export const compareDeductibles = (
  a: client.DeductibleSummary,
  b: client.DeductibleSummary
): number =>
  DEDUCTIBLE_KIND_ORDER.indexOf(a.kind) -
    DEDUCTIBLE_KIND_ORDER.indexOf(b.kind) || a.value - b.value;

const compareNullableLast = (a: number | null, b: number | null): number => {
  if (a == null || b == null) {
    return (a == null ? 1 : 0) - (b == null ? 1 : 0);
  }
  return a - b;
};

/** Orders limits by occurrence, then aggregate, ascending. A missing cap
 *  means no limit, so it sorts last. */
export const compareLimits = (
  a: client.LimitSummary,
  b: client.LimitSummary
): number =>
  compareNullableLast(a.occurrence, b.occurrence) ||
  compareNullableLast(a.aggregate, b.aggregate);
